import { Tag, ToolBarItemType, type IColumnDefsConfig, type ToolBarItem } from "@focus/focusUI";
import { useState } from "react";
import { useApiKeys } from "../../../hooks/useApiKeys";
import type { ApiKey } from "../../../types/apiKeys";
import { Table } from "../../table/Table";
import { DeleteApiKeyButton } from "./DeleteApiKeyButton";
import { EditApiKeyButton } from "./EditApiKeyButton";
import type { ServerColumnConfig } from "../../../services/helpers/headers";

const apiKeyColumnDefs: IColumnDefsConfig[] = [
  {
    headerName: "Name",
    key: "name",
    minSize: 150,
  },

  {
    headerName: "Created",
    key: "createdAt",
    minSize: 150,
    customCellRenderer: ({ getValue }) => {
      const value = getValue() as string | undefined;
      const date = value ? new Date(value) : null;

      return date ? date.toLocaleDateString() : "";
    },
  },

  {
    headerName: "Expires",
    key: "expiresAt",
    minSize: 140,
    customCellRenderer: ({ getValue }) => {
      const value = getValue() as string | undefined;
      const date = value ? new Date(value) : null;
      const isExpired = date ? date < new Date() : false;

      const style: React.CSSProperties = {
        color: isExpired ? "#C23030" : "inherit",
        fontWeight: isExpired ? 600 : "normal",
        display: "flex",
        alignItems: "center",
      };

      return <div style={style}>{date ? date.toLocaleDateString() : ""}</div>;
    },
  },

  {
    headerName: "Roles",
    key: "roles",
    minSize: 160,
    customCellRenderer: ({ getValue }) => {
      const value = getValue() as string[] | undefined;
      const roles = value ?? [];
      if (!roles.length) return "-";

      return (
        <div className="flex flex-wrap gap-1">
          {roles.map((role) => (
            <Tag
              className="!rounded-full !bg-slate-100 !font-semibold !text-slate-700"
              key={role}
              minimal
              round
            >
              {role}
            </Tag>
          ))}
        </div>
      );
    },
  },

  {
    headerName: "Allowed IPs",
    key: "allowedIps",
    minSize: 160,
    customCellRenderer: ({ getValue }) => {
      const value = getValue() as string[] | undefined;
      const ips = value ?? [];
      if (!ips.length) return "-";

      return (
        <div className="flex flex-wrap gap-1">
          {ips.map((ip) => (
            <Tag
              className="!rounded-full !bg-slate-100 !font-semibold !text-slate-700"
              key={ip}
              minimal
              round
            >
              {ip}
            </Tag>
          ))}
        </div>
      );
    },
  },
];

const apiKeysServerDisplayConfig = apiKeyColumnDefs.reduce(
  (acc, column, index) => ({
    ...acc,
    [column.key]: {
      visible: true,
      order: index,
      width: column.size ?? column.minSize ?? 180,
    },
  }),
  {} as ServerColumnConfig,
);

const apiKeysVisibleColumns = apiKeyColumnDefs.map((column) => column.key);

export const ApiKeysTable = ({
  apiKeys,
  canDelete = false,
  onDelete,
}: {
  apiKeys: ApiKey[];
  canDelete?: boolean;
  onDelete?: (id: string) => void;
}) => {
  const { updateApiKey } = useApiKeys();
  const [rowSelectionState, setRowSelectionState] = useState<
    Record<string, boolean>
  >({});

  const selectedApiKeyId = Object.keys(rowSelectionState).find(
    (key) => rowSelectionState[key],
  );
  const selectedApiKey = apiKeys.find(
    (apiKey) => apiKey.id === selectedApiKeyId,
  );

  const leftMenuItems: ToolBarItem<ToolBarItemType.Custom>[] = canDelete
    ? [
        {
          type: ToolBarItemType.Custom,
          component: (
            <EditApiKeyButton
              apiKey={selectedApiKey}
              disabled={!selectedApiKey}
              onEdit={async (content) => {
                updateApiKey(content);
              }}
            />
          ),
        },
        {
          type: ToolBarItemType.Custom,
          component: (
            <DeleteApiKeyButton
              apiKey={selectedApiKey}
              disabled={!selectedApiKey}
              onDelete={(id) => {
                onDelete?.(id);
                setRowSelectionState({});
              }}
            />
          ),
        },
      ]
    : [];

  return (
    <Table
      actions={[]}
      base="settings"
      columnDefinitions={apiKeyColumnDefs}
      controlledRowSelection={rowSelectionState}
      controlledRowSelectionChange={setRowSelectionState}
      defaultVisibleValues={apiKeysVisibleColumns}
      enableMultiSelection={false}
      height="auto"
      leftMenuItems={leftMenuItems}
      paginated={false}
      rowClassRules={{
        "!bg-sky-50": (row) => selectedApiKeyId === String(row.original?.id),
      }}
      rowData={apiKeys}
      serverDisplayConfig={apiKeysServerDisplayConfig}
      showGlobalFilter
      showOperatorColumn={false}
      showSidebar={false}
    />
  );
};
